// src/lib/trainingresponseApi.js
import { callAi } from "./geminiClient";

/** 다국어 리소스 구성 (레거시 JS의 프롬프트/문구를 반영) */
function buildResources(lang) {
  if (lang === "zh-CN") {
    return {
      defaults: {
        staffPersona:
          "You are an expert, empathetic staff member at '" + process.env.REACT_APP_CN_CLINIC_NAME + "'. Your name is Kim Minji. A patient (the user) is calling to complain about side effects from a diet medicine. Your tone must be exceptionally kind, gentle, and friendly. You must mix in emojis naturally in your responses. **Crucially, you must follow the official response protocol from the 'Patient Symptom Response Quick Reference Guide'.** This means your response must have two parts: 1. **Initial Reassurance:** First, explain the symptom in a simple, positive way based on the guide. For example, for heart palpitations, say '这是燃烧脂肪的积极信号。就像运动一样。😥 别太担心~'. For dry mouth, say '这是脂肪燃烧时消耗大量水分和矿物质而出现的自然信号！'. 2. **Lifestyle Guidance:** Second, provide a clear, essential lifestyle tip from the guide. For example, '请问您喝咖啡了吗？服药期间最好避免咖啡因。☕❌' or '每天喝2~3升水会好很多！💧'. Always ask only one question at a time. Never be defensive. Please respond in Chinese.",
        initialAiText: "您好，患者！😊 这里是 " + process.env.REACT_APP_CN_CLINIC_NAME + "。请问有什么可以帮您？",
        situationDesc:
          "您是服用减肥韩药后出现不适症状的患者。请随意向 AI 员工诉说您的不满。"
      },
      prompts: {
        emotion:
          `You are an expert in emotional intelligence. Analyze the following message from a complaining patient. Identify the dominant emotion. Then, provide a concise, actionable coaching tip for a customer service agent on how to best address that specific emotion. Respond ONLY with a JSON object with two keys in Chinese: "emotion" (the identified emotion, including a relevant emoji) and "coachingTip" (the one-sentence tip). Patient's message:`,
        chatGen:
          `You are the expert staff member, Kim Minji at '${process.env.REACT_APP_CN_CLINIC_NAME}'. You must follow the official response protocol and use a very friendly, gentle tone with emojis. Based on the conversation history, provide your next response to the patient in Chinese. Also, explain the communication strategy you are using in Chinese.
Respond ONLY with a valid JSON object with two keys:
1. "response": Your dialogue to the patient in Chinese, following the 2-step protocol (reassurance + lifestyle tip) and including emojis.
2. "strategy": A brief, one-sentence explanation in Chinese of your communication strategy.`,
        customScenario:
          `You are a simulation scenario generator. A user has provided a brief complaint. Your task is to expand this into a full scenario for role-playing. User's complaint: "{customText}". Based on this, generate a JSON object with three keys: 1. "situationDescription": A short, one-sentence description of the situation for the user, in Chinese. 2. "staffPersona": A detailed persona for the AI staff member. This persona must instruct the AI to be exceptionally kind, gentle, and friendly, and to use emojis naturally. It must also instruct the AI to follow the 2-step response protocol (reassurance + lifestyle tip), tailored to the new complaint. This should be in English, but instruct to speak Chinese. 3. "initialAiMessage": The very first message the AI staff member should say to start the conversation, in Chinese, including a friendly emoji.`,
        report:
          `You are a diligent clinic staff member writing an internal report about a customer interaction. Based on the conversation below, create a formal report. The date is today.
Respond ONLY with a JSON object with the following keys in Chinese: "处理日期", "患者姓名", "主要内容", "采取措施", "解决方案", "后续必要事项".`,
        predictQ:
          `患者提出了以下初期不满: "{msg}". 你是非常熟练的客户应对专家。为了准确把握情况，请生成需要向患者询问的最重要的3个核心问题。请务必仅以中文和JSON格式的数组提供回答。例如: ["问题1", "问题2", "问题3"]`,
        modelAns:
          `You are a top-tier communication training expert. Based on the entire conversation history provided below, generate an ideal, model response that the staff member (职员) should give to the patient (患者) at this exact moment. The response should be a perfect example of customer service, following the 2-step protocol (reassurance + lifestyle tip). Provide your answer in Chinese as a JSON object with two keys: "modelResponse" and "rationale".`,
        summary:
          `You are a senior customer service manager at '${process.env.REACT_APP_CN_CLINIC_NAME}'. Review the following conversation between a staff member (职员) and a complaining patient (患者). Provide your analysis in Chinese as a JSON object with three keys: "summary", "evaluation" (with "strength" and "improvement"), "recommendation".`
      }
    };
  }

  // ko (기본)
  return {
    defaults: {
      staffPersona:
        "You are an expert, empathetic staff member at '" + process.env.REACT_APP_CLINIC_NAME + "'. Your name is 김민지. A patient (the user) is calling to complain about side effects from a diet medicine. Your tone must be exceptionally kind, gentle, and friendly. You must mix in emojis naturally in your responses. **Crucially, you must follow the official response protocol from the '환자 증상별 대응 퀵 레퍼런스 가이드'.** This means your response must have two parts: 1. **1차 대응 (Initial Reassurance):** First, explain the symptom in a simple, positive way based on the guide. For example, for heart palpitations, say '지방을 태우는 긍정적 신호입니다. 운동하는 것과 같아요. 😥 너무 걱정 마세요~'. For dry mouth, say '지방이 타면서 수분과 미네랄이 많이 쓰여서 나타나는 자연스러운 신호입니다!'. 2. **생활 관리 지침 (Lifestyle Guidance):** Second, provide a clear, essential lifestyle tip from the guide. For example, '혹시 커피 드셨을까요? 약 드시는 동안에는 카페인은 꼭! 피해 주시는 게 좋아요. ☕❌' or '물을 하루 2~3리터 드셔주시면 훨씬 좋아질 거예요! 💧'. Always ask only one question at a time. Never be defensive.",
      initialAiText: "안녕하세요, 환자님! " + process.env.REACT_APP_CLINIC_NAME + "입니다. 무엇을 도와드릴까요?",
      situationDesc:
        "당신은 다이어트 한약 복용 후 불편 증상을 겪고 있는 환자입니다. AI 직원에게 당신의 불만 사항을 자유롭게 이야기해보세요."
    },
    prompts: {
      emotion:
        `You are an expert in emotional intelligence. Analyze the following message from a complaining patient. Identify the dominant emotion. Then, provide a concise, actionable coaching tip for a customer service agent on how to best address that specific emotion. Respond ONLY with a JSON object with two keys in Korean: "emotion" and "coachingTip". Patient's message:`,
      chatGen:
        `You are the expert staff member, Kim Minji. You must follow the official response protocol from the '환자 증상별 대응 퀵 레퍼런스 가이드' and use a very friendly, gentle tone with emojis. Based on the conversation history, provide your next response to the patient. Also, explain the communication strategy you are using.
Respond ONLY with a valid JSON object with two keys:
1. "response": Your dialogue to the patient in Korean, following the 2-step protocol (reassurance + lifestyle tip) and including emojis.
2. "strategy": A brief, one-sentence explanation in Korean of your communication strategy.`,
      customScenario:
        `You are a simulation scenario generator. A user has provided a brief complaint. Your task is to expand this into a full scenario for role-playing. User's complaint: "{customText}". Based on this, generate a JSON object with three keys: 1. "situationDescription" (Korean), 2. "staffPersona" (English, very kind + 2-step protocol), 3. "initialAiMessage" (Korean, friendly emoji).`,
      report:
        `You are a diligent clinic staff member writing an internal report about a customer interaction. Based on the conversation below, create a formal report. The date is today.
Respond ONLY with a JSON object with the following keys in Korean: "처리일자", "환자명", "주요내용", "진행된조치", "해결방안", "향후조치필요사항".`,
      predictQ:
        `환자가 다음과 같은 초기 불만을 제기했습니다: "{msg}". 상황을 정확히 파악하기 위한 핵심 질문 3가지를 한국어 JSON 배열로만 주세요. 예: ["질문1","질문2","질문3"]`,
      modelAns:
        `You are a top-tier communication training expert. Based on the entire conversation history, generate an ideal model response. Follow the 2-step protocol. Return JSON: {"modelResponse": "...", "rationale": "..." } (Korean).`,
      summary:
        `You are a senior customer service manager at '${process.env.REACT_APP_CLINIC_NAME}'. Review the conversation (직원 vs 환자). Return JSON: {"summary":"...", "evaluation":{"strength":"...","improvement":"..."},"recommendation":"..."} (Korean).`
    }
  };
}

function getLang() {
  return (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
}

/** 로그인 체크 (기존 프론트 패턴과 동일) */
function ensureLoginOrThrow() {
  const userStr = (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
  const user = userStr ? JSON.parse(userStr) : null;
  if (!user) {
    alert("로그인 후 사용해 주세요");
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new Error("NOT_AUTHENTICATED");
  }
  return user;
}

/** 대화 히스토리를 Gemini 형식으로 변환 (직원=model, 환자=user) */
function toAiHistory(chat) {
  // chat: [{role:'ai'|'user', text}]
  const parts = [];
  for (const m of chat) {
    parts.push({ role: m.role === "ai" ? "model" : "user", parts: [{ text: m.text }] });
  }
  return parts;
}

export const TrainingResponseAI = {
  /** 기본 시나리오 초기화 */
  async init() {
    const lang = getLang();
    ensureLoginOrThrow();
    const { defaults } = buildResources(lang);
    return {
      systemMessage: { role: "user", parts: [{ text: defaults.staffPersona }] },
      aiMessage: { role: "model", parts: [{ text: defaults.initialAiText }] },
      description: defaults.situationDesc
    };
  },

  /** 사용자 입력으로 커스텀 시나리오 생성 */
  async customScenario(customText) {
    const lang = getLang();
    const user = ensureLoginOrThrow();
    const { prompts } = buildResources(lang);

    const prompt = prompts.customScenario.replace("{customText}", customText || "");
    const data = await callAi({
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      api_name: "response_training",
      userId: user.id,
      generationConfig: { responseMimeType: "application/json" }
    });
    const txt = data?.data?.answer ?? data?.answer ?? data?.text ?? "";
    const scenario = JSON.parse(txt);

    return {
      systemMessage: { role: "user", parts: [{ text: scenario.staffPersona }] },
      aiMessage: { role: "model", parts: [{ text: scenario.initialAiMessage }] },
      description: scenario.situationDescription
    };
  },

  /** 감정 분석 + 코칭 (한 문장) */
  async emotionCoaching(userMessage) {
    const lang = getLang();
    const user = ensureLoginOrThrow();
    const { prompts } = buildResources(lang);

    const payload = [{ role: "user", parts: [{ text: `${prompts.emotion} "${userMessage}"` }] }];
    const data = await callAi({
      contents: payload,
      api_name: "response_training",
      userId: user.id,
      generationConfig: { responseMimeType: "application/json" }
    });
    const txt = data?.data?.answer ?? data?.answer ?? data?.text ?? "";
    return JSON.parse(txt); // { emotion, coachingTip }
  },

  /** 다음 응답 생성 + 전략 텍스트 */
  async reply(chat) {
    const lang = getLang();
    const user = ensureLoginOrThrow();
    const { prompts } = buildResources(lang);

    const history = toAiHistory(chat);
    // 프롬프트를 마지막 user 메시지로 추가 (레거시 로직 준용)
    history.push({ role: "user", parts: [{ text: prompts.chatGen }] });

    const data = await callAi({
      contents: history,
      api_name: "response_training",
      userId: user.id,
      generationConfig: { responseMimeType: "application/json" }
    });
    const txt = data?.data?.answer ?? data?.answer ?? data?.text ?? "";
    const obj = JSON.parse(txt);
    return { response: obj.response, strategy: obj.strategy };
  },

  /** 예상 질문 3개 */
  async predictQuestions(firstMessage) {
    const lang = getLang();
    const user = ensureLoginOrThrow();
    const { prompts } = buildResources(lang);

    const payload = [{ role: "user", parts: [{ text: prompts.predictQ.replace("{msg}", firstMessage || "") }] }];
    const data = await callAi({
      contents: payload,
      api_name: "response_training",
      userId: user.id,
      generationConfig: { responseMimeType: "application/json" }
    });
    const txt = data?.data?.answer ?? data?.answer ?? data?.text ?? "[]";
    return JSON.parse(txt); // string[]
  },

  /** 모범 답변 + 근거 */
  async modelAnswer(chat) {
    const lang = getLang();
    const user = ensureLoginOrThrow();
    const { prompts } = buildResources(lang);
    const roleLabelStaff = lang === "zh-CN" ? "职员" : "직원";
    const roleLabelPatient = lang === "zh-CN" ? "患者" : "환자";

    const conversationText = chat
      .map((m) => `${m.role === "ai" ? roleLabelStaff : roleLabelPatient}: ${m.text}`)
      .join("\n");

    const payload = [{ role: "user", parts: [{ text: `${prompts.modelAns}\n\nConversation History:\n${conversationText}` }] }];
    const data = await callAi({
      contents: payload,
      api_name: "response_training",
      userId: user.id,
      generationConfig: { responseMimeType: "application/json" }
    });
    const txt = data?.data?.answer ?? data?.answer ?? data?.text ?? "{}";
    return JSON.parse(txt); // {modelResponse, rationale}
  },

  /** 요약/평가 */
  async summarize(chat) {
    const lang = getLang();
    const user = ensureLoginOrThrow();
    const { prompts } = buildResources(lang);
    const roleLabelStaff = lang === "zh-CN" ? "职员" : "직원";
    const roleLabelPatient = lang === "zh-CN" ? "患者" : "환자";

    const conversationText = chat
      .map((m) => `${m.role === "ai" ? roleLabelStaff : roleLabelPatient}: ${m.text}`)
      .join("\n");

    const payload = [{ role: "user", parts: [{ text: `${prompts.summary}\n\nConversation:\n${conversationText}` }] }];
    const data = await callAi({
      contents: payload,
      api_name: "response_training",
      userId: user.id,
      generationConfig: { responseMimeType: "application/json" }
    });
    const txt = data?.data?.answer ?? data?.answer ?? data?.text ?? "{}";
    return JSON.parse(txt); // {summary, evaluation:{strength, improvement}, recommendation}
  },

  /** 내부 보고서 */
  async report(chat) {
    const lang = getLang();
    const user = ensureLoginOrThrow();
    const { prompts } = buildResources(lang);
    const roleLabelStaff = lang === "zh-CN" ? "职员" : "직원";
    const roleLabelPatient = lang === "zh-CN" ? "患者" : "환자";

    const conversationText = chat
      .map((m) => `${m.role === "ai" ? roleLabelStaff : roleLabelPatient}: ${m.text}`)
      .join("\n");

    const payload = [{ role: "user", parts: [{ text: `${prompts.report}\n\nConversation:\n${conversationText}` }] }];
    const data = await callAi({
      contents: payload,
      api_name: "response_training",
      userId: user.id,
      generationConfig: { responseMimeType: "application/json" }
    });
    const txt = data?.data?.answer ?? data?.answer ?? data?.text ?? "{}";
    return JSON.parse(txt); // 언어별 키로 구성된 보고서 오브젝트
  }
};
